/**
 * WebSocket接続管理
 * 設計書に基づいたWebSocketクライアント実装
 */

export interface WebSocketMessage {
  type: string;
  payload?: any;
}

export type WebSocketMessageHandler = (message: WebSocketMessage) => void;
export type BinaryDataHandler = (data: ArrayBuffer) => void;

export interface WebSocketClientOptions {
  heartbeatIntervalMs?: number;
  maxReconnectAttempts?: number;
  reconnectDelayMs?: number;
  /**
   * 指数バックオフの遅延上限(ms)。再接続試行が増えても、この値を超えて待たない。
   * モバイルの長時間瞬断でも一定間隔で粘り強く再接続するための上限。
   */
  reconnectMaxDelayMs?: number;
  /**
   * 直前の ping に対する pong が来ないまま、この時間を超えたら接続を死亡(half-open)とみなし
   * 能動的に close して再接続フローに載せる。0 以下で無効化。
   */
  pongTimeoutMs?: number;
}

type ClientEventName =
  | 'ws_connect_start'
  | 'ws_open'
  | 'ws_error'
  | 'ws_close'
  | 'ws_reconnect_start'
  | 'ws_reconnect_success'
  | 'ws_reconnect_failed'
  | 'ws_send_failed'
  | 'ws_visibility_change'
  | 'ws_online'
  | 'ws_offline'
  | 'ws_pagehide'
  | 'ws_pageshow'
  | 'ws_pong_timeout'
  | 'ws_visibility_reconnect'
  | 'ws_manual_reconnect'
  | 'ws_session_ready'
  | 'ws_session_invalidated'
  | 'ws_session_rebind_request'
  | 'ws_session_wait_timeout'
  | 'ws_binary_drop_session_not_ready'
  | 'ws_stale_close_ignored';

function isWebSocketDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('wsDebug') === '1' || window.localStorage.getItem('dentalAvatarWsDebug') === '1';
  } catch {
    return false;
  }
}

function debugLog(...args: unknown[]): void {
  if (isWebSocketDebugEnabled()) {
    console.log(...args);
  }
}

function debugWarn(...args: unknown[]): void {
  if (isWebSocketDebugEnabled()) {
    console.warn(...args);
  }
}

function isJsonLikeArrayBuffer(data: ArrayBuffer): boolean {
  const bytes = new Uint8Array(data);
  let index = 0;

  while (index < bytes.length) {
    const byte = bytes[index];
    if (byte === 0x20 || byte === 0x09 || byte === 0x0a || byte === 0x0d) {
      index++;
      continue;
    }
    return byte === 0x7b || byte === 0x5b;
  }

  return false;
}

function createConnectionId(): string {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `ws-${Date.now()}-${randomPart}`;
}

/** 接続の外から診断用の記録を送る（sendBeacon → fetch）。面接の流れを止めない。 */
export function postClientEvent(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const body = JSON.stringify({
    source: 'websocket',
    pageUrl: window.location.href,
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    timestamp: new Date().toISOString(),
    ...payload,
  });

  try {
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        '/api/client-events',
        new Blob([body], { type: 'application/json' })
      );
      if (sent) return;
    }
  } catch {
    // fetch fallback below
  }

  fetch('/api/client-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'same-origin',
  }).catch(() => {
    // telemetry must never break the interview flow
  });
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private messageHandlers: Map<string, WebSocketMessageHandler[]> = new Map();
  private binaryHandler: BinaryDataHandler | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts: number;
  private reconnectDelayMs: number;
  private reconnectMaxDelayMs: number;
  private heartbeatIntervalMs: number;
  private pongTimeoutMs: number;
  private heartbeatTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private lastPongAt: number | null = null;
  private lastPingSentAt: number | null = null;
  private lastReceivedType: string | null = null;
  private lastReceivedAt: number | null = null;
  private lastSentType: string | null = null;
  private lastSentAt: number | null = null;
  private currentSessionId: string | null = null;
  private currentInterviewId: string | number | null = null;
  private connectionId = createConnectionId();
  private sessionReady = false;
  private sessionReadyWaiters: Array<(ready: boolean) => void> = [];
  private shouldReconnect = true;
  private reconnectCallback: (() => void) | null = null;
  private disconnectCallback: ((info: { code: number; willReconnect: boolean }) => void) | null = null;

  constructor(url: string, options: WebSocketClientOptions = {}) {
    this.url = url;
    this.heartbeatIntervalMs = options.heartbeatIntervalMs ?? 5_000;
    // タイプB対策: モバイルの長い瞬断（トンネル・エレベータ・画面ロック・長時間バックグラウンド・
    // 回線の持続的な遮断）でも自動復旧で吸収できるよう、再接続を粘り強くする。
    // 指数バックオフ(上限あり)+jitter で、上限到達(最終 reload 案内)まで体感 ~30 分弱まで試み続ける。
    // 加えて online / visibilitychange 復帰時は recoverOnVisible() が即時再接続するため、
    // 実用上はネットワーク復帰とほぼ同時に復旧する。
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 60;
    this.reconnectDelayMs = options.reconnectDelayMs ?? 1_000;
    this.reconnectMaxDelayMs = options.reconnectMaxDelayMs ?? 30_000;
    this.pongTimeoutMs = options.pongTimeoutMs ?? 12_000;
    this.registerLifecycleTelemetry();
  }

  getCurrentInterviewId(): number | null {
    const parsed = Number(this.currentInterviewId);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  /**
   * WebSocket接続を開始
   */
  connect(): Promise<void> {
    this.shouldReconnect = true;
    this.clearReconnectTimer();

    return new Promise((resolve, reject) => {
      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}${this.url}`;
        this.emitClientEvent('ws_connect_start', { wsUrl });
        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        let settled = false;

        this.ws = ws;

        ws.onopen = () => {
          debugLog('[WebSocketClient] Connected');
          this.emitClientEvent('ws_open');
          this.reconnectAttempts = 0;
          this.lastPingSentAt = null;
          this.lastPongAt = null;
          this.startHeartbeat();
          if (!settled) {
            settled = true;
            resolve();
          }
        };

        ws.onmessage = async (event) => {
          if (typeof event.data === 'string') {
            this.handleTextData(event.data, 'string');
          } else if (event.data instanceof ArrayBuffer) {
            if (isJsonLikeArrayBuffer(event.data)) {
              this.handleTextData(new TextDecoder().decode(event.data), 'ArrayBuffer');
            } else {
              this.handleBinaryData(event.data);
            }
          } else if (event.data instanceof Blob) {
            const arrayBuffer = await event.data.arrayBuffer();
            if (isJsonLikeArrayBuffer(arrayBuffer)) {
              this.handleTextData(new TextDecoder().decode(arrayBuffer), 'Blob');
            } else {
              this.handleBinaryData(arrayBuffer);
            }
          } else {
            console.error('[WebSocketClient] Unsupported message data:', event.data);
          }
        };

        ws.onerror = (error) => {
          console.error('[WebSocketClient] Error:', error);
          this.emitClientEvent('ws_error', {
            errorType: error instanceof Event ? error.type : typeof error,
          });
          if (!settled) {
            settled = true;
            reject(error);
          }
        };

        ws.onclose = (event) => {
          if (this.ws !== ws) {
            this.emitClientEvent('ws_stale_close_ignored', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            });
            debugWarn('[WebSocketClient] Ignored stale close from non-current socket');
            return;
          }

          debugLog('[WebSocketClient] Disconnected');
          this.emitClientEvent('ws_close', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
          this.markSessionNotReady('socket_close', true);
          this.stopHeartbeat();
          this.ws = null;

          if (!settled) {
            settled = true;
            reject(new Error(`WebSocket closed before opening: ${event.code}`));
          }

          // 意図的な切断(disconnect())以外は、UI 復帰のために通知する。
          // モバイルでは WS が切れても固まらない（再生待ち/再生中のまま固定しない）ことが重要。
          if (this.shouldReconnect) {
            const willReconnect = this.reconnectAttempts < this.maxReconnectAttempts;
            try {
              this.disconnectCallback?.({ code: event.code, willReconnect });
            } catch (callbackError) {
              console.error('[WebSocketClient] disconnect callback error:', callbackError);
            }
          }

          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const attempt = this.reconnectAttempts;
            const delayMs = this.computeReconnectDelay(attempt);
            debugLog(`[WebSocketClient] Reconnecting (attempt ${this.reconnectAttempts}, delay ${delayMs}ms)...`);
            this.emitClientEvent('ws_reconnect_start', {
              attempt,
              delayMs,
            });

            this.reconnectTimer = window.setTimeout(() => {
              this.reconnectTimer = null;
              this.connect().then(() => {
                debugLog('[WebSocketClient] Reconnected successfully');
                this.emitClientEvent('ws_reconnect_success', {
                  attempt,
                });
                if (this.reconnectCallback) {
                  this.reconnectCallback();
                }
              }).catch((error) => {
                this.emitClientEvent('ws_reconnect_failed', {
                  attempt,
                  errorMessage: error instanceof Error ? error.message : String(error),
                });
                console.error(error);
              });
            }, delayMs);
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * WebSocket接続を閉じる
   */
  disconnect(): void {
    this.shouldReconnect = false;
    this.stopHeartbeat();
    this.clearReconnectTimer();
    this.markSessionNotReady('disconnect', true);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 現在の接続を異常切断として閉じ、通常の自動再接続経路を通す。
   * モバイル復旧の動作確認など、利用者操作によらず再接続を開始したい時に使う。
   */
  forceReconnect(reason = 'manual-reconnect'): boolean {
    if (!this.ws || (this.ws.readyState !== WebSocket.OPEN && this.ws.readyState !== WebSocket.CONNECTING)) {
      return false;
    }
    this.shouldReconnect = true;
    this.ws.close(4000, reason.slice(0, 120));
    return true;
  }

  /**
   * メッセージを送信
   */
  send(message: WebSocketMessage): void {
    this.rememberOutgoingMessage(message);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocketClient] WebSocket is not connected');
      this.emitClientEvent('ws_send_failed', {
        attemptedType: message.type,
        readyState: this.ws?.readyState ?? null,
      });
    }
  }

  /**
   * バイナリデータを送信
   */
  sendBinary(data: ArrayBuffer): void {
    this.lastSentType = 'binary';
    this.lastSentAt = Date.now();
    if (!this.sessionReady) {
      console.warn('[WebSocketClient] Session is not ready; dropping binary audio data');
      this.emitClientEvent('ws_binary_drop_session_not_ready', {
        attemptedType: 'binary',
        bytes: data.byteLength,
        readyState: this.ws?.readyState ?? null,
      });
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.warn('[WebSocketClient] WebSocket is not connected');
      this.emitClientEvent('ws_send_failed', {
        attemptedType: 'binary',
        bytes: data.byteLength,
        readyState: this.ws?.readyState ?? null,
      });
    }
  }

  /**
   * メッセージハンドラーを登録
   */
  on(type: string, handler: WebSocketMessageHandler): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  /**
   * メッセージハンドラーを削除
   */
  off(type: string, handler: WebSocketMessageHandler): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * バイナリデータハンドラーを設定
   */
  setBinaryHandler(handler: BinaryDataHandler | null): void {
    this.binaryHandler = handler;
  }

  /**
   * メッセージを処理
   */
  private handleTextData(data: string, source: 'string' | 'Blob' | 'ArrayBuffer'): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      this.lastReceivedType = message.type;
      this.lastReceivedAt = Date.now();
      debugLog(`[WebSocketClient] Message received (from ${source}):`, message.type, message.payload);
      this.handleMessage(message);
    } catch (error) {
      console.error(`[WebSocketClient] Failed to parse message from ${source}:`, error, data);
    }
  }

  private handleBinaryData(data: ArrayBuffer): void {
    this.lastReceivedType = 'binary';
    this.lastReceivedAt = Date.now();
    if (this.binaryHandler) {
      this.binaryHandler(data);
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    if (message.type === 'pong') {
      this.lastPongAt = Date.now();
      return;
    }

    if (message.type === 'connected') {
      this.markSessionReady();
    }

    debugLog('[WebSocketClient] handleMessage called:', message.type, 'handlers:', this.messageHandlers.has(message.type));
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      debugLog('[WebSocketClient] Calling', handlers.length, 'handler(s) for type:', message.type);
      handlers.forEach(handler => handler(message));
    } else {
      debugWarn('[WebSocketClient] No handlers registered for message type:', message.type);
      debugLog('[WebSocketClient] Registered handlers:', Array.from(this.messageHandlers.keys()));
    }
  }

  /**
   * 再接続完了時のコールバックを設定
   */
  onReconnect(callback: () => void): void {
    this.reconnectCallback = callback;
  }

  /**
   * 切断時のコールバックを設定。意図しない切断（再接続を試みる切断）で発火する。
   * UI 側で再生待ち/再生中の状態を復帰させ、マイクを再有効化するために使う。
   */
  onDisconnect(callback: (info: { code: number; willReconnect: boolean }) => void): void {
    this.disconnectCallback = callback;
  }

  /**
   * 接続状態を取得
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * WebSocket が open しているだけでなく、server 側 connect_session が受理済みか。
   * 録音開始・音声バイナリ送信はこの状態になるまで待つ。
   */
  isSessionReady(): boolean {
    return this.isConnected() && this.sessionReady;
  }

  /**
   * 接続を保証する。既に接続済みなら即 true。
   * 未接続なら（自動再接続の試行回数が尽きていても）能動的に張り直し、
   * 成功時は onReconnect コールバック（connect_session 再送）を発火させる。
   * マイク押下など「ユーザーが操作した瞬間に確実につなぎ直したい」場面で使う。
   */
  async ensureConnected(timeoutMs = 8_000): Promise<boolean> {
    if (this.isConnected()) return true;

    this.shouldReconnect = true;

    // 既に接続確立中（自動再接続が進行中）なら、その完了を待つ。
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      return this.waitForOpen(timeoutMs);
    }

    // 未接続。自動再接続のバックオフや試行上限を無視して、その場で1回つなぎ直す。
    this.emitClientEvent('ws_manual_reconnect', { reconnectAttempts: this.reconnectAttempts });
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    try {
      await this.connect();
      if (this.reconnectCallback) {
        this.reconnectCallback();
      }
      return this.isConnected();
    } catch (error) {
      console.error('[WebSocketClient] ensureConnected failed:', error);
      return false;
    }
  }

  /**
   * WebSocket open 後、server の connected ack まで待つ。
   * モバイル再接続中は OPEN 直後でも server 側の session/STT が未準備なことがあるため、
   * recognition_start の直前でこの確認を使う。
   */
  async ensureSessionReady(timeoutMs = 8_000): Promise<boolean> {
    const connected = await this.ensureConnected(timeoutMs);
    if (!connected) return false;
    if (this.sessionReady) return true;
    this.requestSessionBindingRefresh('ensure_session_ready');

    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        this.sessionReadyWaiters = this.sessionReadyWaiters.filter((waiter) => waiter !== resolveReady);
        this.emitClientEvent('ws_session_wait_timeout', { timeoutMs });
        resolve(false);
      }, timeoutMs);

      const resolveReady = (ready: boolean) => {
        window.clearTimeout(timeout);
        resolve(ready);
      };

      this.sessionReadyWaiters.push(resolveReady);
    });
  }

  private waitForOpen(timeoutMs: number): Promise<boolean> {
    if (this.isConnected()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (this.isConnected()) {
          window.clearInterval(timer);
          resolve(true);
        } else if (Date.now() - startedAt >= timeoutMs) {
          window.clearInterval(timer);
          resolve(false);
        }
      }, 100);
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = window.setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }

      // half-open 検知: 直前に送った ping への pong が来ないまま pongTimeoutMs を超えていれば、
      // 接続は死亡しているとみなして能動的に close する（onclose → 既存の再接続フローに載る）。
      // バックグラウンドで JS が止められている間はこのタイマー自体が走らないため、誤検知しない。
      if (this.pongTimeoutMs > 0 && this.lastPingSentAt !== null) {
        const pongReceivedForLastPing = this.lastPongAt !== null && this.lastPongAt >= this.lastPingSentAt;
        const elapsedSincePing = Date.now() - this.lastPingSentAt;
        if (!pongReceivedForLastPing && elapsedSincePing >= this.pongTimeoutMs) {
          this.emitClientEvent('ws_pong_timeout', { elapsedSincePingMs: elapsedSincePing });
          debugWarn('[WebSocketClient] pong timeout, closing to trigger reconnect');
          try {
            this.ws.close();
          } catch (closeError) {
            console.error('[WebSocketClient] pong-timeout close failed:', closeError);
          }
          return;
        }
      }

      this.lastPingSentAt = Date.now();
      this.ws.send(JSON.stringify({
        type: 'ping',
        payload: {
          timestamp: Date.now(),
          lastPongAt: this.lastPongAt,
        },
      }));
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 再接続の待機時間(ms)を指数バックオフ + jitter で算出する。
   * base * 2^(attempt-1) を上限 reconnectMaxDelayMs でクランプし、
   * thundering herd（多数端末が同時に再接続を試みて瞬間的にサーバへ集中）を避けるため
   * 最大 +20% のランダム jitter を加える。attempt は 1 始まり。
   */
  private computeReconnectDelay(attempt: number): number {
    const exp = this.reconnectDelayMs * 2 ** Math.max(0, attempt - 1);
    const capped = Math.min(this.reconnectMaxDelayMs, exp);
    const jitter = capped * 0.2 * Math.random();
    return Math.round(capped + jitter);
  }

  /**
   * タブが前面復帰した瞬間の回復処理。
   * モバイルではバックグラウンド中に WS が黙って切れている / JS が止められていることが多く、
   * onclose や再接続タイマーが走らないまま固まりやすい。復帰時に状態を点検して、
   * 未接続なら即時再接続、接続中なら ping を打って half-open を素早く炙り出す。
   */
  private recoverOnVisible(): void {
    if (!this.shouldReconnect) return;

    // 接続確立中（CONNECTING）は二重接続を避けるため何もしない。
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;

    if (this.isConnected()) {
      // 接続中に見えても half-open の可能性があるため、即 ping して pong 監視の起点を更新する。
      this.lastPingSentAt = Date.now();
      try {
        this.ws?.send(JSON.stringify({ type: 'ping', payload: { timestamp: Date.now(), lastPongAt: this.lastPongAt } }));
      } catch {
        // 送信できない＝実質切断。下の再接続判定に委ねるため close する。
        try { this.ws?.close(); } catch { /* noop */ }
      }
      return;
    }

    // 未接続。再接続待ちのバックオフを待たず、即座に1回つなぎ直す。
    this.emitClientEvent('ws_visibility_reconnect', { reconnectAttempts: this.reconnectAttempts });
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.connect().then(() => {
      if (this.reconnectCallback) {
        this.reconnectCallback();
      }
    }).catch((error) => {
      console.error('[WebSocketClient] visibility reconnect failed:', error);
    });
  }

  private rememberOutgoingMessage(message: WebSocketMessage): void {
    this.lastSentType = message.type;
    this.lastSentAt = Date.now();

    if (message.type === 'connect_session') {
      this.markSessionNotReady('connect_session_sent');
    }

    const payload = message.payload;
    if (payload && typeof payload === 'object') {
      if (typeof payload.sessionId === 'string') {
        this.currentSessionId = payload.sessionId;
      }
      const interviewId = payload.interviewId ?? payload.interview_id;
      if (typeof interviewId === 'string' || typeof interviewId === 'number') {
        this.currentInterviewId = interviewId;
      }
    }
  }

  private emitClientEvent(event: ClientEventName, details: Record<string, unknown> = {}): void {
    const now = Date.now();
    postClientEvent({
      event,
      sessionId: this.currentSessionId ?? undefined,
      interviewId: this.currentInterviewId ?? undefined,
      details: {
        connectionId: this.connectionId,
        url: this.url,
        readyState: this.ws?.readyState ?? null,
        bufferedAmount: this.ws?.bufferedAmount ?? null,
        reconnectAttempts: this.reconnectAttempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
        lastPongAgeMs: this.lastPongAt ? now - this.lastPongAt : null,
        lastReceivedType: this.lastReceivedType,
        lastReceivedAgeMs: this.lastReceivedAt ? now - this.lastReceivedAt : null,
        lastSentType: this.lastSentType,
        lastSentAgeMs: this.lastSentAt ? now - this.lastSentAt : null,
        sessionReady: this.sessionReady,
        ...details,
      },
    });
  }

  private markSessionReady(): void {
    if (!this.sessionReady) {
      this.sessionReady = true;
      this.emitClientEvent('ws_session_ready');
    }
    const waiters = this.sessionReadyWaiters.splice(0);
    for (const waiter of waiters) {
      waiter(true);
    }
  }

  invalidateSessionReady(reason = 'manual'): void {
    this.markSessionNotReady(reason, true);
  }

  requestSessionBindingRefresh(reason = 'manual'): boolean {
    this.markSessionNotReady(reason);
    this.emitClientEvent('ws_session_rebind_request', { reason });
    if (!this.isConnected() || !this.reconnectCallback) {
      return false;
    }
    try {
      this.reconnectCallback();
      return true;
    } catch (error) {
      console.error('[WebSocketClient] session binding refresh failed:', error);
      return false;
    }
  }

  private markSessionNotReady(reason = 'unknown', resolveWaiters = false): void {
    const wasReady = this.sessionReady;
    this.sessionReady = false;
    if (wasReady) {
      this.emitClientEvent('ws_session_invalidated', { reason });
    }
    if (resolveWaiters) {
      const waiters = this.sessionReadyWaiters.splice(0);
      for (const waiter of waiters) {
        waiter(false);
      }
    }
  }

  private registerLifecycleTelemetry(): void {
    if (typeof window === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      this.emitClientEvent('ws_visibility_change');
      if (document.visibilityState === 'visible') {
        this.recoverOnVisible();
      }
    });
    window.addEventListener('online', () => {
      this.emitClientEvent('ws_online');
      // タイプB対策: OS がネットワーク復帰を検知した瞬間に、状態を点検して必要なら即時再接続する。
      // 回線の持続的な遮断で再接続バックオフ待ち / half-open のまま固まっていても、
      // ここで強制的に復旧フローへ載せることで、ユーザー操作なしに自動復旧できる。
      this.recoverOnVisible();
    });
    window.addEventListener('offline', () => {
      this.emitClientEvent('ws_offline');
    });
    window.addEventListener('pagehide', () => {
      this.emitClientEvent('ws_pagehide');
    });
    window.addEventListener('pageshow', () => {
      this.emitClientEvent('ws_pageshow');
    });
  }
}
